import { Modal, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import { CommonModal } from '@sketch-note/design-system/src/components/modal';
import { StandardSnack } from '@sketch-note/design-system/src/components/snackbar';
import { Input } from '@sketch-note/design-system/src/components/text-input';
import { TileButtonTwo } from '@sketch-note/design-system/src/components/tile-button';
import { ErrorIcon, InfoIcon } from '@sketchnote/icons';
import { Button, DropdownTwo, LoaderSm } from '@sketchnote/shared-components';
import cn from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useSocketSyncOrg } from 'src/api';
import { useSettings as useMoreSettings } from 'src/api/hooks/fetch-settings';
import Analytics from 'src/api/services/analytics/analytics';
import { useAuth } from 'src/shared/hooks/use-auth';
import { useLayout } from 'src/shared/hooks/use-layout';

import { useProfileStore, useSettings } from '../store';
import { ChangeEmail, ChangePassword, DeleteAccount } from './components';
import { DisplayOption } from './components/display-options';
import { PictureUpload } from './components/picture-upload';

export const Profile = ({ onClose }: { onClose: () => void }): JSX.Element => {
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [loaderIsOpen, setLoaderIsOpen] = useState(false);

  const [optionModalIsOpen, setOptionModalIsOpen] = useState(false);
  const [isChangePassword, setIsChangePassword] = useState(false);
  const [isChangeEmail, setIsChangeEmail] = useState(false);
  const [snackbarModalIsOpen, setSnackbarModalIsOpen] = useState(false);
  const [errorSnackbarModalIsOpen, setErrorSnackbarModalIsOpen] = useState(false);
  const [callbacks] = useSettings();
  const { revalidate } = useMoreSettings();
  const [open] = useSnackbar();

  const {
    setName,
    name,
    setLastName,
    lastName,
    careerPaths,
    setCareerPaths,
    emailId,
    profileImage,
    setProfileImage,
    profileImageSize,
  } = useProfileStore((state) => state);
  const { onLogout } = useLayout();
  // useEffect(() => {
  //   setFirstNameLocal(name);
  //   setLastNameLocal(lastName);
  // }, []);
  const handleDeleteModalClose = () => {
    setDeleteModalIsOpen(false);
  };

  const handleSnackbarModal = () => {
    setSnackbarModalIsOpen(false);
  };
  const handleErrorSnackbarModal = () => {
    setErrorSnackbarModalIsOpen(false);
  };
  const handleDeleteModalOpen = () => {
    setDeleteModalIsOpen(true);
  };
  const handleOptionOpen = () => {
    setOptionModalIsOpen(true);
  };
  const handleLoaderModal = () => {
    setLoaderIsOpen(false);
  };

  const handleOptionClose = () => {
    setOptionModalIsOpen(false);
  };
  const handleChangePasswordModalClose = () => {
    setIsChangePassword(false);
  };
  const handleChangePasswordModalOpen = () => {
    setIsChangePassword(true);
  };

  const handleChangeEmail = () => {
    setIsChangeEmail(false);
  };
  const handleChangeEmailModalOpen = () => {
    setIsChangeEmail(true);
  };

  const handleNameChange = useCallback((e) => {
    setName(e.target.value);
  }, []);
  const handleLastNameChange = useCallback((e) => {
    setLastName(e.target.value);
  }, []);

  const handleSingleSelectDropdown = (item, value) => {
    const temp = { ...careerPaths };
    temp[item].answer = value;
    const selections = temp[item].options
      .map((x, index) => (x.label === value.label ? index : null))
      .filter((item) => item != null);
    temp[item].answer = value;
    temp[item].answerIndex = selections;
    Analytics.people.update_user_info(careerPaths[item]?.answer?.label, item);
    Analytics.track('Career Path Update', {
      'Updated Question': careerPaths[item]?.question,
      'Updated Answer': careerPaths[item]?.answer.label,
    });
    setCareerPaths(temp);
  };

  const handleSaveChanges = async () => {
    if (profileImageSize > 3145728) {
      open({
        text: 'Oops! File too large, try again!',
        duration: 2000,
        position: 'top-center',
        snackType: 'error',
      });
    } else {
      setLoaderIsOpen(true);
      const info = {};
      try {
        if (name && lastName) {
          // Object.keys(careerPaths).map(
          //   (item) => (info[careerPaths[item].identifier] = careerPaths[item].answerIndex)
          // );

          const response = await callbacks?.onSaveProfile?.({
            name,
            lastName,
            info,
            profileImage,
          });

          if (response.isSuccess) {
            open({
              text: 'Changes Saved',
              duration: 2000,
              position: 'top-center',
              snackType: 'standard',
            });
          }
        } else {
          open({
            text: 'Please enter First Name and Last Name',
            duration: 2000,
            position: 'top-center',
            snackType: 'error',
          });
        }
      } catch (error) {
        console.error(error);
        open({
          text: 'Oops! Something went wrong while saving changes',
          duration: 2000,
          position: 'top-center',
          snackType: 'error',
        });
      }
      setLoaderIsOpen(false);
    }
  };

  const handleLogOut = () => {
    onLogout();
  };

  const authData = useAuth((s) => s.userDetails);
  const socketSyncOrg = useSocketSyncOrg({
    organisation: authData?.memberInformation?.organisation?.organisationId,
  });

  useEffect(() => {
    revalidate();
  }, [socketSyncOrg]);

  return (
    <div>
      <div className="overflow-y-auto px-48 " style={{ height: '52rem' }}>
        <section className="border-b border-gray-200 pt-16 pb-48">
          <p className="mb-20 text-lg leading-22  text-black font-medium">Profile</p>
          <div className=" flex text-xs leading-18 font-regular text-gray-500">
            <p className="mt-1">
              <InfoIcon />
            </p>
            <p>
              &nbsp;Changes made to profile settings will also apply to all the organizations
              you&apos;re a part of.&nbsp;
            </p>
          </div>
        </section>

        <section className="border-b border-gray-200 py-48">
          <span className="mb-32 block font-medium text-black">Avatar</span>
          <PictureUpload
            onImageChange={(imageURL) => {
              setProfileImage(imageURL);
            }}
          />
        </section>

        <section className="border-b border-gray-200 py-48">
          <span className="mb-32 text-base block font-medium text-black">Personal Info</span>
          <p className="mb-20 text-sm font-regular text-black">Email</p>
          <div className="flex items-center mt-20 mb-32">
            <p className="text-base leading-22 font-regular">{emailId}</p>
            {/* <button className="text-sm ml-20 text-primary-500" onClick={handleChangeEmailModalOpen}>
              Change email
            </button> */}
          </div>

          <div className="flex items-center mt-32 ">
            <Input
              wrapperClassName="settings-input pt-7 pb-7 px-12 bg-gray-100"
              label="First Name"
              placeholder="First name"
              value={name}
              onChange={handleNameChange}
            />

            <Input
              wrapperClassName="settings-input pt-7 pb-7 px-12 ml-24 bg-gray-100"
              labelClassName="ml-24"
              label="Last Name"
              placeholder="Last name"
              value={lastName}
              onChange={handleLastNameChange}
            />
          </div>
        </section>

        {/* <section className="border-b border-gray-200 py-48 ">
          <div>
            <label
              className="mb-8 block text-base leading-20 font-medium"
              htmlFor="profile_password">
              Password
            </label>
            <p className="text-sm mb-16 text-gray-600 font-regular" id="profile_password">
              Choose a strong password <br />
              Changing your password will log you out. Log in with your new password
            </p>
            <Button onClick={handleChangePasswordModalOpen}>Change password</Button>
          </div>
        </section> */}

        {/* <section className="border-b border-gray-200 py-48">
          <div>
            <label className="mb-8 block font-medium text-black" htmlFor="profile_career">
              Career path
            </label>
            <p className="text-sm  text-gray-600" id="profile_career">
              Keeping this section updated will help you find modules most relevant to you
            </p>
          </div>

          <div className="flex items-center flex-wrap">
            {Object.keys(careerPaths).map((item, index) =>
              careerPaths[item] &&
              careerPaths[item].type &&
              (careerPaths[item].type === 'single-select' ||
                careerPaths[item].type === 'single-dropdown') ? (
                <div
                  className={cn({
                    'mt-32 w-full mr-32': true,
                  })}
                  key={`${careerPaths[item].question}-${index}`}
                >
                  <p className="mb-8 block text-sm text-gray-600">{careerPaths[item].question}</p>
                  <DropdownTwo
                    key={careerPaths[item].question}
                    className="w-full"
                    label={careerPaths[item].question}
                    value={careerPaths[item].answer}
                    placeholder={'Select an option'}
                    options={careerPaths[item].options}
                    onChange={handleSingleSelectDropdown.bind(this, item)}
                  />
                </div>
              ) : null
            )}
          </div>

          {
          //  <div className="mt-32">
          //   {Object.keys(careerPaths).map((item, index) =>
          //     careerPaths[item].type === 'multi-select' ? (
          //       <div className="mt-32 text-gray-600" key={`${careerPaths[item].question}-${index}`}>
          //         <div className="flex">
          //           <p className="block text-sm">{careerPaths[item].question}&nbsp;</p>
          //           <button className="text-blue-500 text-sm" onClick={handleOptionOpen}>
          //             Update
          //           </button>
          //         </div>
          //         <div className={'grid grid-cols-2 gap-y-24 mt-8 '}>
          //           {careerPaths[item]?.answer?.map((option, index) => (
          //             <TileButtonTwo className="w-284" key={`${option.value}-${index}`}>
          //               {option.label}
          //             </TileButtonTwo>
          //           ))}
          //         </div>
          //       </div>
          //     ) : null
          //   )}
          // </div> 
        }
        </section> */}

        <section className=" border-gray-200 py-48">
          <span className="mb-8 text-base block font-medium text-black">Log Out</span>
          <p className="text-sm mb-22 text-gray-600 max-w-336">
            You will be logged out of all other active sessions including this one and will have to
            log back in
          </p>
          <Button status="danger" onClick={handleLogOut}>
            Log out
          </Button>
        </section>

        {process.env.NODE_ENV !== 'production' && (
          <section className=" border-t py-48">
            <span className="mb-8 block font-medium text-base text-black">Tread carefully!</span>
            <p className="text-sm mb-22 text-gray-600 max-w-336">
              You will lose all your lessons, creations, and docs. This action can&apos;t be
              reversed
            </p>
            <Button status="danger" onClick={handleDeleteModalOpen}>
              Delete account
            </Button>
          </section>
        )}
      </div>
      <div className="sticky flex justify-end mt-10 px-64 py-12 border-t border-gray-200 ">
        <Button status="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button status="primary" className="ml-10" onClick={handleSaveChanges}>
          Save changes
        </Button>
      </div>
      <CommonModal
        isOpen={optionModalIsOpen}
        onClose={handleOptionClose}
        contentStyle={{ width: '720px' }}
      >
        <DisplayOption onClose={handleOptionClose} />
      </CommonModal>

      <DeleteAccount isOpen={deleteModalIsOpen} onClose={handleDeleteModalClose} />

      <CommonModal
        isOpen={isChangePassword}
        headText="Change your password"
        onClose={handleChangePasswordModalClose}
        contentStyle={{ width: '460px' }}
      >
        <ChangePassword onClose={handleChangePasswordModalClose} />
      </CommonModal>
      <CommonModal
        isOpen={isChangeEmail}
        onClose={handleChangeEmail}
        contentStyle={{ width: '522px' }}
      >
        <ChangeEmail onClose={handleChangeEmail} />
      </CommonModal>
      <Modal isOpen={loaderIsOpen} onClose={handleLoaderModal}>
        <ModalOverlay />
        <ModalContent style={{ width: '38.4rem', padding: '30px', marginTop: '20rem' }}>
          <p className="font-semibold mb-20"> We&apos;re saving your changes </p>
          <LoaderSm className="mx-auto text-xss" />
        </ModalContent>
      </Modal>
      <Modal isOpen={snackbarModalIsOpen} onClose={handleSnackbarModal}>
        <ModalContent style={{ width: '180px', borderRadius: '10px', marginTop: '65rem' }}>
          <StandardSnack text="Changes Saved" />
        </ModalContent>
      </Modal>
      <Modal isOpen={errorSnackbarModalIsOpen} onClose={handleErrorSnackbarModal}>
        <ModalContent style={{ width: '40rem', borderRadius: '10px', marginTop: '65rem' }}>
          <StandardSnack icon={<ErrorIcon />} text="Oops! File too large, try again" />
        </ModalContent>
      </Modal>
    </div>
  );
};
